import { Link, useNavigate } from 'react-router-dom';

import url from '../../helpers/url';
import { ButtonTypes, COMPONENT_TYPES, PROGRAM_TYPE } from '../../config/constants';
import { __ } from '../../translations';
import { shouldShowParentalRatingPopup } from '../../helpers/common';
import viewEvents from '../../helpers/viewEvents';

import { Button } from '@intigral/shared/components/Button';
import { FontIcon } from '@intigral/shared/components/FontIcon';
import { setSession } from '@intigral/utils/lib/browser-storage/localStorage';
import { getPromotionRoutePath } from '@intigral/utils/lib/helpers/promotion';
import { SESSION_WCP } from '@intigral/constant';
import { appConfig } from '../../config/app.config';
import { ParentalLock } from '../elements/ParentalLock';
import useToggleDetailOverlay from '../../hooks/useToggleDetailOverlay';
import useLanguage from '../../hooks/useLanguage';
import usePlayVideoClick from '../../hooks/usePlayVideoClick';
import {
    getLinearContinueWatchRoute,
    getVodContinueWatchRoute,
} from '../../features/Continue-watching-carousel/utils';

interface PlayVideoButtonProps {
    children?: any;
    className?: string;
    buttonType?: string;
    item: any;
    componentType?: string;
    componentName?: string;
    meta?: any;
    onClick?: () => void;
    nextEpisode?: any;
    fromOverlay?: boolean;
    testId?: string;
    shouldCallMixPanelEvent?: boolean;
    variant?: 'link' | 'button';
    isLoading?: boolean;
    iconOnly?: boolean;
    compact?: boolean;
}

const PlayVideoButton = ({
    children,
    className,
    buttonType,
    item,
    componentType,
    componentName,
    meta,
    testId,
    onClick = () => {
        return;
    },
    nextEpisode = null,
    fromOverlay = false,
    shouldCallMixPanelEvent = true,
    variant = 'link',
    isLoading = false,
    iconOnly = false,
    compact = false,
}: PlayVideoButtonProps) => {
    const navigate = useNavigate();
    const language = useLanguage();
    const { showDetailOverlay, removeSearchParams, showPpvOverlay } = useToggleDetailOverlay();
    const handlePlayClick = usePlayVideoClick({
        buttonType,
        componentType,
        componentName,
        meta,
        testId,
        onClick,
        shouldCallMixPanelEvent,
    });

    if (!item || !Object.keys(item).length) {
        return null;
    }

    const onclickHandler = (e) => {
        handlePlayClick(e, item);
    };

    const getPlayLink = (buttonType, _componentName, item) => {
        const urlQueryParams = {
            id: btoa(item?.Id || item?.id || item?.paId),
            guid: btoa(item.guid),
        } as { season?: any };

        const seasonNumber = item?.tvSeasonNumber || item?.seasonNumber;
        if (item?.programType === PROGRAM_TYPE.EPISODE && seasonNumber) {
            urlQueryParams.season = seasonNumber;
        }

        if (buttonType === 'VodItem-details-overlay') {
            return (
                <>
                    <Button
                        plain
                        LinkComponent={Link}
                        to={url.route('play', urlQueryParams)}
                        state={item}
                        className={className}
                        onClick={onclickHandler}
                        data-test-id={testId}
                        loading={isLoading}
                    >
                        {children}
                        <FontIcon name="play" size="large" md="x-large" />
                    </Button>
                </>
            );
        }

        if (iconOnly) {
            return (
                <Button
                    primary
                    social
                    compact
                    LinkComponent={Link}
                    to={url.route('play', urlQueryParams)}
                    className={className}
                    onClick={onclickHandler}
                    data-test-id={testId}
                    loading={isLoading}
                    aria-label={typeof children === 'string' ? children : __('play_now')}
                >
                    <FontIcon name="play" size="smaller" md="smaller" rotate={language === 'ar' ? 'flip' : null} />
                </Button>
            );
        }

        return (
            <Button
                primary
                LinkComponent={Link}
                to={url.route('play', urlQueryParams)}
                className={className}
                onClick={onclickHandler}
                compact
                data-test-id={testId}
                loading={isLoading}
            >
                {children}
                <FontIcon
                    name="play"
                    size="smaller"
                    rotate={language === 'ar' ? 'flip' : null}
                    md="smaller"
                ></FontIcon>
            </Button>
        );
    };

    const openDetailOverlay = () => {
        removeSearchParams(['movie', 'series', 'guid', 'season']);
        const itemId = item.Id;
        const typeKey = item.programType === 'movie' ? 'movie' : 'series';
        showDetailOverlay({
            [typeKey]: btoa(itemId),
            guid: btoa(item?.guid),
            season: btoa(item?.tvSeasonNumber || item?.seasonNumber || null),
        });
    };

    /**
     * Renders either a styled Button or a Link component based on the provided `variant` prop.
     * This function is supposed to replace big `switch` statement in the original code
     *
     * @param link - The destination URL or route for the button or link.
     * @returns A React element: either a Button or Link, configured with the provided props.
     *
     * @todo additional variants will be added to support more button/link types
     *
     * @remarks
     * - If `variant` is `'button'`, returns a Button component with navigation and click handling.
     * - If `variant` is `'link'` or any other value, returns a Link component.
     * - Both components receive `className`, `onClick` handler, `testId`, and `children` props.
     */
    const renderButton = (link: string, state?) => {
        const defaultProps = {
            className,
            onClick: onclickHandler,
        };

        if (testId) {
            defaultProps['data-test-id'] = testId;
        }

        if (state) {
            defaultProps['state'] = state;
        }

        switch (variant) {
            case 'button':
                return (
                    <Button
                        primary
                        {...defaultProps}
                        LinkComponent={Link}
                        to={link}
                        compact={componentType !== COMPONENT_TYPES.FEATURED_SLIDER}
                        className={className}
                    >
                        {children}
                    </Button>
                );
            case 'link':
            default:
                return (
                    <Link {...defaultProps} to={link}>
                        {children}
                    </Link>
                );
        }
    };

    switch (buttonType) {
        // TODO: is this used anywhere?
        case ButtonTypes.NextEpisode:
            return renderButton(
                url.route('play', {
                    id: btoa(nextEpisode.id),
                    season: nextEpisode.seasonNumber || '',
                }),
                nextEpisode
            );
        // TODO: is this used anywhere?
        case ButtonTypes.SeriesDetails:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                    season: item.seasonNumber || '',
                }),
                item
            );

        case ButtonTypes.VodDetails:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                }),
                item
            );
        case ButtonTypes.Stream:
        case ButtonTypes.TvGuideCarouselItemLive:
            return (
                <Link
                    to={url.route('livetv', {
                        channelId: btoa(item.channelId),
                    })}
                    className={className}
                    onClick={onclickHandler}
                >
                    {children}
                </Link>
            );
        case ButtonTypes.VodFeaturedItem:
            if (!item?.promoType) return;
            if (item.promoType === 'PPV') {
                return (
                    <Button
                        primary
                        className={className}
                        data-test-id={testId}
                        onClick={(e) => {
                            showPpvOverlay({
                                eventId: item?.ppvEvent?.eventId,
                                type: 'Home_Screen',
                            });
                            onclickHandler(e);
                        }}
                    >
                        {children}
                    </Button>
                );
            } else if (['CHANNEL', 'LIVESTREAM', 'PPV'].includes(item.promoType)) {
                return (
                    <Button
                        primary
                        to={url.route('livetv', {
                            channelId: btoa(item.channelId),
                        })}
                        className={className}
                        onClick={onclickHandler}
                        LinkComponent={Link}
                        data-test-id={testId}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'REWIND') {
                return (
                    <Button
                        primary
                        to={url.route('livetv', {
                            id: btoa(item.Id),
                            stationId: btoa(item.stationId),
                            listingId: btoa(item.listingId),
                        })}
                        className={className}
                        onClick={(e) => {
                            setSession(SESSION_WCP.PROMO_REWIND, item);
                            onclickHandler(e);
                        }}
                        LinkComponent={Link}
                        data-test-id={testId}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'CATCHUP') {
                const title = language === 'ar' ? item?.programTitle?.ar || '' : item?.programTitle?.en || '';
                const channelTitle =
                    language === 'ar' ? item?.channel?.channelTitleAr || '' : item?.channel?.channelTitle || '';
                return (
                    <Button
                        primary
                        className={className}
                        data-test-id={testId}
                        onClick={(e) => {
                            e.preventDefault();
                            onclickHandler(e);
                            if (item?.ratings && shouldShowParentalRatingPopup(item?.ratings)) {
                                viewEvents.broadcast('openPopup', {
                                    component: <ParentalLock />,
                                    screen: 'catchupPlayer',
                                });
                                return;
                            }
                            navigate(`${language ? `/${language}` : ''}/catchup-player`, {
                                state: {
                                    programData: {
                                        title: title,
                                        name: title,
                                        startTime: item?.startTime || 0,
                                        endTime: item?.endTime || 0,
                                        tuningURLs: {
                                            catchUpURL: item?.channel?.tuningURLs?.catchUpURL || '',
                                        },
                                        channelTitle: channelTitle,
                                        channelName: channelTitle,
                                        channelNumber: item?.channel?.channelNumber,
                                        ...(item?.channel?.category &&
                                            Array.isArray(item.channel.category) &&
                                            item.channel.category[0] && {
                                                channelCategory: item.channel.category[0],
                                            }),
                                        poster: item?.image,
                                        guid: item?.guid,
                                        paId: item?.paId,
                                    },
                                },
                            });
                        }}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'SEASON') {
                return (
                    <Button
                        primary
                        to={url.route('play', {
                            id: btoa(item.Id),
                            guid: btoa(item?.seriesId?.split('/')?.pop()),
                            season: item.seasonNumber,
                        })}
                        className={className}
                        onClick={onclickHandler}
                        LinkComponent={Link}
                        data-test-id={testId}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'ROUTE' && item.screenId) {
                return (
                    <Button
                        primary
                        className={className}
                        data-test-id={testId}
                        onClick={(e) => {
                            e.preventDefault();
                            onclickHandler(e);
                            navigate(getPromotionRoutePath(item.screenId, language, appConfig.defaultLanguage));
                        }}
                    >
                        {children}
                    </Button>
                );
            } else {
                return (
                    <Button
                        primary
                        onClick={onclickHandler}
                        LinkComponent={Link}
                        to={url.route('play', { id: btoa(item.Id), guid: btoa(item.guid) })}
                        data-test-id={testId}
                        className={className}
                    >
                        {children}
                    </Button>
                );
            }
        case ButtonTypes.EpisodeItem:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                    season: item.tvSeasonNumber ? item.tvSeasonNumber : item.seasonNumber,
                }),
                item
            );
        case ButtonTypes.RewindItem:
            return renderButton(
                url.route(url.getDetailsPage('live'), {
                    id: btoa(item.paId || item.Id),
                    stationId: btoa(item.stationId),
                    listingId: btoa(item.listingId),
                    startDate: btoa(item?.startTime),
                }),
                { rewindItem: item, isRewindItem: true }
            );
        case ButtonTypes.ContinueWatching:
            return item.category === 'LINEAR'
                ? renderButton(getLinearContinueWatchRoute(item), { rewindItem: item, isRewindItem: true })
                : renderButton(getVodContinueWatchRoute(item), item);
        case ButtonTypes.WatchlistCarouselItem:
            return item.programType === 'Rewind'
                ? renderButton(
                      url.route(url.getDetailsPage('live'), {
                          id: btoa(item.Id),
                          stationId: btoa(item.stationId),
                          startDate: btoa(item?.startTime),
                      })
                  )
                : renderButton(
                      url.route('play', {
                          id: btoa(item.Id),
                          guid: btoa(item.guid),
                      }),
                      item
                  );
        case ButtonTypes.VodeItem:
        case 'VodItem-details-overlay':
            return getPlayLink(buttonType, componentName, item);
        case ButtonTypes.TVodItem:
            return fromOverlay ? (
                <Button
                    primary
                    compact={compact}
                    onClick={onclickHandler}
                    state={item}
                    className={'primary play-video'}
                    data-test-id={testId}
                >
                    {children}
                </Button>
            ) : (
                <Button
                    primary
                    compact={compact}
                    onClick={onclickHandler}
                    LinkComponent={Link}
                    to={fromOverlay ? null : url.route('play', { id: btoa(item.Id), guid: btoa(item.guid) })}
                    state={item}
                    className={'primary play-video'}
                    data-test-id={testId}
                >
                    {children}
                </Button>
            );
        case ButtonTypes.TvGuideCarouselItemRewind:
        case ButtonTypes.ProgramItem:
            return renderButton(
                url.route(url.getDetailsPage('live'), {
                    id: btoa(item.paId || item.Id),
                    stationId: btoa(item.stationId),
                    startDate: btoa(item?.startTime),
                })
            );
        case ButtonTypes.PPVItem:
            return renderButton(
                url.route('livetv', {
                    channelId: btoa(meta.ppvInfo.channelId),
                })
            );
        case ButtonTypes.IsTVODItem:
            return (
                <Button primary onClick={openDetailOverlay} className={className}>
                    {' '}
                    {children}
                </Button>
            );
        default:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                }),
                item
            );
    }
};

export default PlayVideoButton;
